/**
 * A video conversion that never ended (1.174.0).
 *
 * From the field: "bei sehr großen Videos bricht am iPhone die Konvertierung
 * ohne Fehlermeldung ab." When iOS ends the web app for memory, no `catch`
 * runs and no line reaches the diagnosis, which lives in memory too; what is
 * left is a reloaded page that says nothing. So a mark goes on the device
 * before the conversion and comes off after it, however it ends. A mark found
 * at the next start is a conversion that was cut off from outside.
 *
 * `localStorage` and not the outbox: the mark must survive exactly the case in
 * which nothing else is written any more, and it is one line.
 */

const KEY = 'tb-video-conversion'

export interface UnfinishedConversion {
  name: string
  bytes: number
  /** When it began, ISO. */
  at: string
}

export function beginConversion(file: File): void {
  try {
    const mark: UnfinishedConversion = { name: file.name, bytes: file.size, at: new Date().toISOString() }
    localStorage.setItem(KEY, JSON.stringify(mark))
  } catch {
    // Private mode or a full store: the conversion runs anyway, only the
    // report after a crash is missing.
  }
}

export function endConversion(): void {
  try {
    localStorage.removeItem(KEY)
  } catch {
    // See above.
  }
}

/** The mark of a conversion that was cut off, taken off the device; null if there is none. */
export function takeUnfinishedConversion(): UnfinishedConversion | null {
  try {
    const raw = localStorage.getItem(KEY)
    if (raw === null) return null
    localStorage.removeItem(KEY)
    const mark = JSON.parse(raw) as Partial<UnfinishedConversion>
    if (typeof mark.name !== 'string' || typeof mark.bytes !== 'number' || typeof mark.at !== 'string') return null
    return { name: mark.name, bytes: mark.bytes, at: mark.at }
  } catch {
    return null
  }
}
